import { Link } from "wouter";
import { SEO } from "@/components/SEO";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { NetworkRouter } from "@/components/NetworkRouter";
import { NetworkNextStep } from "@/components/NetworkNextStep";
import { motion } from "framer-motion";
import { breadcrumbSchema, faqSchema, medicalOrganizationSchema, personSchema, publisherOrgSchema, websiteSchema, webPageSchema, PRESS_FEATURES } from "@/lib/seo";
import { withNetworkUtm } from "@/lib/network";
const headshot = "/img/headshot.webp";
const bookCover = "/img/book-cover-front.webp";
const shrinkMDLogo = "/img/shrinkmd-logo.webp";
const shrinQLogo = "/img/shrinq-logo-on-dark.png";
const unstuckLogo = "/img/unstuck-logo.webp";

export default function Home() {
  const SEO_TITLE =
    "Shariq Refai, MD, MBA, Board-Certified Psychiatrist";
  const SEO_DESCRIPTION =
    "Shariq Refai, MD, MBA, board-certified psychiatrist and founder of shrinkMD and The Shrink Network. Author, medical reviewer, and maker of mental health tools.";

  const homePage = webPageSchema({
    path: "/",
    title: SEO_TITLE,
    description: SEO_DESCRIPTION,
    type: "ProfilePage",
    image: "/og-home.jpg",
    speakableSelectors: [".at-a-glance"],
    datePublished: "2026-04-01",
    dateModified: "2026-09-21",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Shariq Refai", path: "/#person" },
    ],
  });

  const homeFaq = faqSchema([
    {
      question: "Who is Shariq Refai, MD, MBA?",
      answer:
        "Shariq Refai, MD, MBA, is a board-certified psychiatrist with more than fifteen years of experience in psychiatry and mental health care. He's the founder of shrinkMD and shrinQ, the creator of the Unstuck app, and the author of upcoming books on anxiety, overthinking, and emotional health. He's based in Jacksonville, Florida.",
    },
    {
      question: "What is shrinkMD?",
      answer:
        "shrinkMD is an independent multistate telepsychiatry company founded by Dr. Refai. shrinkMD is a separate clinical entity from shariqrefai.com, which is an educational author and media platform. For clinical care inquiries, please visit shrinkmd.com.",
    },
    {
      question: "What does Shariq Refai write about?",
      answer:
        "Dr. Refai writes about anxiety, overthinking, emotional regulation, the high-performer pattern, why popular mental health commentary often falls short in the moment, and how modern psychiatry actually works. The writing is for adults who've already tried the apps and the books and still can't quiet the noise in their own heads.",
    },
    {
      question: "Where is Shariq Refai based?",
      answer:
        "Dr. Refai is based in Jacksonville, Florida. His telepsychiatry practice shrinkMD operates across multiple states.",
    },
    {
      question: "How can I work with Shariq Refai?",
      answer:
        "shariqrefai.com is an educational author and media platform and does not provide clinical care. For clinical care inquiries, please visit shrinkmd.com. For media, speaking, or publishing inquiries, use the contact page at shariqrefai.com/contact.",
    },
  ]);

  return (
    <SiteLayout>
      <SEO
        title={SEO_TITLE}
        ogTitle="Shariq Refai, MD, MBA"
        twitterTitle="Shariq Refai, MD, MBA"
        description={SEO_DESCRIPTION}
        url="/"
        image="/og-home.jpg"
        keywords="Shariq Refai, psychiatrist Jacksonville, shrinkMD founder, Unstuck app, board-certified psychiatrist, mental health author, anxiety, overthinking"
        schema={[
          personSchema,
          websiteSchema,
          publisherOrgSchema,
          medicalOrganizationSchema,
          homePage,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Shariq Refai", path: "/#person" },
          ]),
          homeFaq,
        ]}
      />

      {/* Hero Section */}
      <section className="hero pt-24 pb-16 md:pt-32 md:pb-24 px-4 md:px-8 container mx-auto">
        <div className="grid md:grid-cols-2 gap-12 lg:gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="flex flex-col gap-6"
          >
            {/* Credibility eyebrow (Master Brief Table 23) */}
            <p className="hero-eyebrow text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-accent">
              Shariq Refai, MD, MBA · Board-Certified Psychiatrist
            </p>
            {/* Outcome-led H1 (Master Brief Table 23; brand name lives in the eyebrow) */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-primary leading-[1.1] tracking-tight">
              Psychiatry should make more sense. I build the places that help it.
            </h1>
            {/* Subhead: offer, intended user, mechanism (Master Brief hero anatomy) */}
            <p className="hero-subhead text-xl md:text-2xl font-serif text-secondary leading-snug">
              Founder of shrinkMD and The Shrink Network. Physician, author,
              medical reviewer, and builder of practical mental-health tools.
            </p>
            {/* Primary + secondary CTA (Master Brief Table 23 + rule "one primary conversion action per page") */}
            <div className="flex flex-wrap gap-4 pt-4">
              <Link
                href="/projects"
                className="inline-flex h-12 items-center justify-center bg-primary text-primary-foreground px-8 text-sm font-medium hover:bg-secondary transition-colors"
              >
                Explore my work
              </Link>
              <Link
                href="/media-inquiries"
                className="inline-flex h-12 items-center justify-center border border-border bg-transparent text-primary px-8 text-sm font-medium hover:bg-accent/5 hover:border-accent transition-colors"
              >
                Media and speaking
              </Link>
            </div>
            {/* Proof strip near the claim (Master Brief Table 23 proof line + hero anatomy) */}
            <ul
              aria-label="Credentials and press"
              className="hero-proof mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs md:text-sm text-muted-foreground list-none p-0"
            >
              <li className="flex items-center gap-2">
                <span aria-hidden="true" className="text-accent">✓</span>
                Board certified
              </li>
              <li aria-hidden="true" className="text-border">·</li>
              <li className="flex items-center gap-2">
                <span aria-hidden="true" className="text-accent">✓</span>
                15+ years in psychiatry
              </li>
              <li aria-hidden="true" className="text-border">·</li>
              <li className="flex items-center gap-2">
                <span aria-hidden="true" className="text-accent">✓</span>
                FAPA
              </li>
              <li aria-hidden="true" className="text-border">·</li>
              <li>
                Quoted in{" "}
                <Link
                  href="/press"
                  className="underline decoration-muted-foreground/40 underline-offset-4 hover:text-primary hover:decoration-primary transition-colors"
                >
                  The Epoch Times, EatingWell, and MedStory
                </Link>
              </li>
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="relative"
          >
            <div className="aspect-[4/3] md:aspect-[4/5] relative overflow-hidden bg-muted max-w-[420px] md:max-w-none mx-auto">
              <img
                src={headshot}
                alt="Shariq Refai, MD, MBA, board-certified psychiatrist and shrinkMD founder, portrait."
                className="object-cover w-full h-full grayscale-[20%] contrast-125"
                width={731}
                height={914}
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-accent/10 -z-10"></div>
          </motion.div>
        </div>
      </section>

      {/* Authority Strip */}
      <section className="credibility-bar border-y border-border/60 bg-muted/30">
        <div className="container mx-auto px-4 md:px-8 py-8">
          <div className="flex flex-wrap justify-center gap-x-8 gap-y-4 text-sm font-medium text-muted-foreground text-center uppercase tracking-widest">
            <span>Board-Certified Psychiatrist</span>
            <span className="hidden md:inline">&bull;</span>
            <span>Founder of shrinkMD</span>
            <span className="hidden md:inline">&bull;</span>
            <span>Founder of shrinQ</span>
            <span className="hidden md:inline">&bull;</span>
            <span>Creator of Unstuck</span>
            <span className="hidden md:inline">&bull;</span>
            <span>Forbes Business Council</span>
            <span className="hidden md:inline">&bull;</span>
            <span>Sports &amp; Performance Psychiatry</span>
          </div>
        </div>
      </section>

      {/* Ecosystem Message — the master framing for The Shrink Network */}
      <section className="ecosystem-message py-16 md:py-20 px-4 md:px-8 bg-secondary text-white">
        <div className="container mx-auto max-w-3xl text-center">
          <span className="text-[#58B8B1] text-xs font-bold tracking-[0.25em] uppercase">
            The Shrink Network
          </span>
          <p className="mt-6 font-serif text-2xl md:text-3xl lg:text-4xl leading-snug text-white">
            Mental health is too important to squeeze into one website.
          </p>
          <p className="mt-8 max-w-2xl mx-auto text-base md:text-lg font-light leading-relaxed text-white/85">
            The Shrink Network is an independent mental health knowledge
            ecosystem. Each site has one job. Together, they help people
            understand, apply, and access care. No matter where you enter the
            network, we help you find the next step that makes sense.
          </p>
          <p className="mt-8 font-serif italic text-lg md:text-xl text-[#58B8B1]">
            Your mind. Understood.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <a
              href={withNetworkUtm("https://shrinknetwork.com/", "home_ecosystem_message")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-12 items-center justify-center bg-white text-secondary px-8 text-sm font-medium hover:bg-[#58B8B1] hover:text-white transition-colors"
            >
              Enter The Shrink Network
            </a>
            <Link
              href="/founder-of-the-shrink-network"
              className="inline-flex h-12 items-center justify-center border border-white/30 bg-transparent text-white px-8 text-sm font-medium hover:bg-white hover:text-secondary transition-colors"
            >
              Why I built it this way
            </Link>
          </div>
        </div>
      </section>

      {/* The Perspective Behind The Shrink Network */}
      <section className="perspective-behind py-20 md:py-24 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            The Perspective
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mt-6 mb-6">
            The perspective behind The Shrink Network.
          </h2>
          <div className="space-y-5 text-lg text-secondary font-light leading-relaxed">
            <p>
              I started one site because patients had questions between visits. I
              built the rest because the work kept revealing more gaps. Now
              there's a clinical practice, several educational publications,
              including{" "}
              {/* ShrinkDaily inbound-contract entry e-0001 (inline-first-mention, campaign=home) */}
              <a
                data-inbound-contract="shrinkdaily/e-0001"
                href="https://shrinkdaily.com/?utm_source=shariqrefai&utm_medium=network_reciprocal&utm_campaign=home"
                rel="noopener"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
              >
                ShrinkDaily
              </a>
              , a structured self-guided program, a couple of practical tools,
              three books, and the founder site you're reading. Each one has its
              own job. All of them are reviewed by the same psychiatrist (me)
              and share the same evidence methodology, the same
              conflict-of-interest disclosures, and the same crisis routing.
            </p>
            <p>
              <a
                href={withNetworkUtm("https://shrinknetwork.com/", "home_page")}
                rel="noopener"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
              >
                The Shrink Network
              </a>{" "}
              is the connective tissue that makes the whole thing legible to a
              reader.
            </p>
          </div>
          {/* ShrinkDaily inbound-contract entry e-0002 (see-also, campaign=atlas) */}
          <aside
            aria-label="See also"
            className="see-also mt-10 border-l-2 border-accent/40 pl-4 py-2 text-sm md:text-base text-secondary font-light"
          >
            <span className="block text-xs font-bold uppercase tracking-[0.2em] text-accent mb-2">
              See also
            </span>
            <p>
              For a map of how the network's sites connect, see{" "}
              <a
                data-inbound-contract="shrinkdaily/e-0002"
                href="https://shrinkdaily.com/atlas.html?utm_source=shariqrefai&utm_medium=network_reciprocal&utm_campaign=atlas"
                rel="noopener"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
              >
                the Shrink Atlas
              </a>
              .
            </p>
          </aside>
          <Link
            href="/founder-of-the-shrink-network"
            className="mt-8 inline-flex items-center text-sm font-medium tracking-wide text-accent hover:text-primary transition-colors"
          >
            Read why I built it &rarr;
          </Link>
        </div>
      </section>

      {/* The Origin */}
      <section className="network-origin py-20 md:py-24 px-4 md:px-8 bg-muted/30 border-y border-border/50">
        <div className="container mx-auto max-w-3xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            The Origin
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mt-6 mb-6">
            The Shrink Network began with a simple problem.
          </h2>
          <div className="space-y-5 text-lg text-secondary font-light leading-relaxed">
            <p>
              shrinkMD began as an attempt to make psychiatric care easier to
              access and easier to understand. The broader Shrink Network grew
              from the same idea: people need different resources at different
              moments. Sometimes they need an explanation. Sometimes they need a
              practical tool. Sometimes they need expert care. A single site
              can't do all of those jobs at once without doing each one worse
              than it could be done alone.
            </p>
            <p>So I built each job its own site. Then I made sure they talk to each other.</p>
          </div>
          <Link
            href="/what-im-building"
            className="mt-8 inline-flex items-center text-sm font-medium tracking-wide text-accent hover:text-primary transition-colors"
          >
            See what I'm building &rarr;
          </Link>
        </div>
      </section>

      {/* At a Glance */}
      <section className="at-a-glance py-20 md:py-24 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-6">
            <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
              At a Glance
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mb-8">
            About Dr. Refai
          </h2>
          <p className="text-lg md:text-xl text-secondary font-light leading-relaxed">
            Dr. Refai is a board-certified psychiatrist based in Jacksonville,
            Florida, with dual board certifications in psychiatry and
            sports/performance psychiatry. He founded the multistate
            telepsychiatry company shrinkMD to expand access to mental
            health care. He created the wellness app Unstuck. He writes books
            and educational essays for the people who've already tried the
            apps, read the books, and still can't quiet the noise in their
            heads. His professional background includes more than fifteen
            years of experience in psychiatry and mental health care, with
            work in professional sports settings, including affiliations
            involving the NFL Substance Abuse Program and the Jacksonville
            Jaguars.
          </p>
          <div className="pt-8">
            <Link
              href="/about"
              className="inline-flex items-center text-sm font-medium tracking-wide text-accent hover:text-primary transition-colors"
            >
              Read the full biography &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Book */}
      <section className="featured-book py-20 md:py-24 bg-muted/30 px-4 md:px-8 border-y border-border/50">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1 flex justify-center md:justify-end">
              <motion.img
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                src={bookCover}
                alt="Cover of upcoming book Your Mind Is Full of Sh*t by Shariq Refai, MD, MBA."
                className="max-w-[280px] md:max-w-[360px] w-full shadow-2xl"
                width={500}
                height={781}
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="order-1 md:order-2 flex flex-col gap-6 max-w-lg">
              <div className="text-sm font-semibold tracking-widest uppercase text-accent">
                Upcoming Release
              </div>
              <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight">
                Your Mind Is Full of Sh*t
              </h2>
              <p className="text-lg text-primary font-medium font-serif italic leading-snug">
                A psychiatrist's guide to understanding overthinking, anxiety,
                and the lies in your head.
              </p>
              <p className="text-base md:text-lg text-secondary font-light leading-relaxed">
                The book takes the patterns Dr. Refai has examined over more
                than fifteen years in psychiatry and lays them out in plain
                language.
                Control. Productivity. Positivity. Happiness. Four lies most of
                us absorbed before we knew we were learning them. The book
                names them, shows the science underneath them, and gives you
                tools you can use on a real Tuesday. Not affirmations. Not
                hacks. Real practices that work in the body the way the body
                actually works.
              </p>
              <div className="pt-2">
                <Link
                  href="/books/your-mind-is-full-of-shit"
                  className="inline-flex h-12 items-center justify-center bg-primary text-primary-foreground px-8 text-sm font-medium hover:bg-secondary transition-colors"
                >
                  View Book Details
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Dr. Refai Writes About */}
      <section className="what-i-write-about py-20 md:py-24 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-6">
            <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
              The Work
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mb-8">
            What Dr. Refai Writes About
          </h2>
          <div className="space-y-6 text-base md:text-lg text-secondary font-light leading-relaxed">
            <p>
              The site covers the topics most modern adults are quietly
              carrying. Anxiety that gets louder when you finally try to rest.
              Overthinking that won't shut off. The high-performer pattern that
              builds careers and quietly costs the people running them. The
              reasons most popular mental health commentary falls short in the
              moment a person actually needs it. What modern psychiatry gets right and what the
              public conversation still gets wrong. The future of digital
              mental health, and why convenience isn't the same as care.
            </p>
            <p>
              Every essay is written by a board-certified psychiatrist for an
              adult audience that's tired of wellness fluff. The tone is human.
              The science is real. The goal is education, not therapy.
            </p>
          </div>
          <div className="pt-8">
            <Link
              href="/perspectives"
              className="inline-flex h-12 items-center justify-center border border-border bg-transparent text-primary px-8 text-sm font-medium hover:bg-accent/5 hover:border-accent transition-colors"
            >
              Read the Essays
            </Link>
          </div>
        </div>
      </section>

      {/* Founder Projects */}
      <section className="founder-projects py-20 md:py-24 bg-[#171717] text-white px-4 md:px-8">
        <div className="container mx-auto">
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <span className="text-[#9B7B55] text-xs font-bold tracking-[0.25em] uppercase mb-4 block">
              Independent Ventures
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-white mb-4">
              Founder Projects
            </h2>
            <p className="text-gray-400 font-light leading-relaxed">
              Independent ventures founded by Shariq Refai in mental health
              and wellness. These are separate organizations from this
              educational platform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <a
              href={withNetworkUtm("https://shrinknetwork.com/", "home_page")}
              rel="noopener"
              className="group flex flex-col gap-6 p-8 border border-[#58B8B1]/30 bg-[#58B8B1]/5 hover:bg-[#58B8B1]/10 transition-colors"
            >
              <div className="h-16 flex items-center justify-center w-full">
                <span className="text-[#58B8B1] text-xs font-bold tracking-[0.25em] uppercase">
                  The Umbrella
                </span>
              </div>
              <h3 className="font-serif text-2xl text-white">Shrink Network</h3>
              <p className="text-sm text-gray-300 font-light leading-relaxed flex-1">
                The umbrella of independent editorial mental health
                publications Dr. Refai founded and medically reviews.
                ShrinkNetwork.com is the front door for the network and routes
                visitors to the right property. It sells nothing, runs no ads,
                and earns no commission.
              </p>
              <span className="text-sm font-medium text-white group-hover:text-[#9B7B55] transition-colors mt-auto">
                Visit Shrink Network &rarr;
              </span>
            </a>

            <a
              href={withNetworkUtm("https://shrinkmd.com/", "home_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col gap-6 p-8 border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
            >
              <div className="h-16 flex items-center justify-center bg-white p-4 w-full">
                <img
                  src={shrinkMDLogo}
                  alt="shrinkMD logo"
                  className="max-h-10 object-contain" width={240} height={65} loading="lazy" decoding="async"/>
              </div>
              <h3 className="font-serif text-2xl text-white">shrinkMD</h3>
              <p className="text-sm text-gray-300 font-light leading-relaxed flex-1">
                A multistate telepsychiatry organization founded by Dr. Refai.
                shrinkMD is an independent clinical entity, separate from
                shariqrefai.com, and is not operated by, affiliated with, or a
                service of this educational site. Listed here for
                informational and disclosure purposes only. Dr. Refai is the
                founder of, and holds a material ownership interest in,
                shrinkMD.
              </p>
              <span className="text-sm font-medium text-white group-hover:text-[#9B7B55] transition-colors mt-auto">
                Learn about shrinkMD &rarr;
              </span>
            </a>

            <a
              href={withNetworkUtm("https://shrinq.com/", "home_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col gap-6 p-8 border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
            >
              <div className="h-16 flex items-center justify-center bg-[#1B2B29] p-4 w-full">
                <img
                  src={shrinQLogo}
                  alt="shrinQ logo"
                  className="max-h-12 object-contain" width={1283} height={345} loading="lazy" decoding="async"/>
              </div>
              <h3 className="font-serif text-2xl text-white">shrinQ</h3>
              <p className="text-sm text-gray-300 font-light leading-relaxed flex-1">
                An eleven-module self-guided digital mental wellness program
                built around four phases: understanding the mind, breaking
                the loops, building durable habits, and burnout and recovery.
                shrinQ is the educational and coaching arm of Dr. Refai's
                work, separate from his clinical practice. It isn't therapy,
                diagnosis, or a substitute for care from a licensed
                clinician.
              </p>
              <span className="text-sm font-medium text-white group-hover:text-[#9B7B55] transition-colors mt-auto">
                Visit shrinQ &rarr;
              </span>
            </a>

            <a
              href={withNetworkUtm("https://beunstuck.app/", "home_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col gap-6 p-8 border border-white/10 bg-white/5 hover:bg-white/10 transition-colors"
            >
              <div className="h-16 flex items-center justify-center bg-[#FDF9F2] p-4 w-full">
                <img
                  src={unstuckLogo}
                  alt="Unstuck App logo"
                  className="max-h-12 object-contain" width={240} height={54} loading="lazy" decoding="async"/>
              </div>
              <h3 className="font-serif text-2xl text-white">Unstuck</h3>
              <p className="text-sm text-gray-300 font-light leading-relaxed flex-1">
                A mental wellness app to support reflection, journaling,
                reminders, and emotional awareness tools. Available on iOS.
              </p>
              <span className="text-sm font-medium text-white group-hover:text-[#9B7B55] transition-colors mt-auto">
                Get the Unstuck App &rarr;
              </span>
            </a>
          </div>
          <div className="text-center mt-12">
            <Link
              href="/projects"
              className="inline-flex items-center text-sm font-medium tracking-wide text-[#9B7B55] hover:text-white transition-colors"
            >
              Explore all founder ventures &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* Founder, Reviewer, Author */}
      <section className="founder-reviewer-author py-20 md:py-24 px-4 md:px-8 bg-muted/30 border-y border-border/50">
        <div className="container mx-auto max-w-3xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            What I Do
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mt-6 mb-6">
            Founder, Reviewer, Author.
          </h2>
          <div className="space-y-5 text-lg text-secondary font-light leading-relaxed">
            <p>
              I'm three things at once: the founder of shrinkMD and the Shrink
              Network properties; the medical reviewer for the editorial
              publications in the network; and the author of essays and books
              written for adults who've already tried the apps and read the books
              and still can't quiet the noise in their own heads. Each role has
              its own page, its own role description, and its own boundary.
            </p>
          </div>
          <div className="mt-8 border-t border-border/60 pt-6">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-accent">
              Medical reviewer for
            </p>
            <p className="mt-3 flex flex-wrap gap-x-4 gap-y-2 text-sm text-secondary font-medium">
              <a
                href={withNetworkUtm("https://anxietyresource.org/contributors/shariq-refai/", "home_reviewer_strip")}
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
              >
                AnxietyResource.org
              </a>
              <span aria-hidden="true" className="text-muted-foreground">&middot;</span>
              <a
                href={withNetworkUtm("https://depressionresource.org/about/shariq-refai/", "home_reviewer_strip")}
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
              >
                DepressionResource.org
              </a>
              <span aria-hidden="true" className="text-muted-foreground">&middot;</span>
              <a
                href={withNetworkUtm("https://anxietyresearch.org/medical-review-board/", "home_reviewer_strip")}
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
              >
                AnxietyResearch.org
              </a>
              <span aria-hidden="true" className="text-muted-foreground">&middot;</span>
              <a
                href={withNetworkUtm("https://psychiatryrx.org/medical-editor/", "home_reviewer_strip")}
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
              >
                PsychiatryRx.org
              </a>
              <span aria-hidden="true" className="text-muted-foreground">&middot;</span>
              <a
                href={withNetworkUtm("https://shrinknetwork.com/", "home_reviewer_strip")}
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
              >
                Shrink Network
              </a>
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              href="/founder-of-the-shrink-network"
              className="inline-flex h-12 items-center justify-center bg-primary text-primary-foreground px-6 text-sm font-medium hover:bg-secondary transition-colors"
            >
              Read the founder narrative
            </Link>
            <Link
              href="/medical-reviewer"
              className="inline-flex h-12 items-center justify-center border border-border bg-transparent text-primary px-6 text-sm font-medium hover:bg-accent/5 hover:border-accent transition-colors"
            >
              See the medical reviewer profile
            </Link>
            <Link
              href="/books"
              className="inline-flex h-12 items-center justify-center border border-border bg-transparent text-primary px-6 text-sm font-medium hover:bg-accent/5 hover:border-accent transition-colors"
            >
              Read the books
            </Link>
          </div>
        </div>
      </section>

      {/* Recognition and Press */}
      <section className="recognition py-20 md:py-24 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-4xl">
          <div className="mb-6">
            <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
              In the Press
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mb-8">
            Recognition and Press
          </h2>
          <p className="text-base md:text-lg text-secondary font-light leading-relaxed mb-8">
            Dr. Refai is a member of the{" "}
            <a
              href="https://councils.forbes.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
            >
              Forbes Business Council
            </a>{" "}
            and a Fellow of the American Psychiatric Association. His
            perspective on psychiatry, sports, and digital mental health has
            been featured in{" "}
            <a
              href="https://topdoctormagazine.com/mental-health/mental-health-does-not-discriminate-an-interview-with-dr-shariq-refai/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
            >
              Top Doctor Magazine
            </a>{" "}
            and the{" "}
            <a
              href="https://www.dcmsonline.org/news/700524/Full-Circle-Calling-Spotlight-on-DCMS-Member-Shariq-Refai-MD-MBA-FAPA.htm"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
            >
              Duval County Medical Society
            </a>
            .
          </p>
          <ul className="space-y-3 mb-8 text-base md:text-lg text-secondary font-light leading-relaxed">
            {PRESS_FEATURES.map((feature) => (
              <li key={feature.url}>
                <a
                  href={feature.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
                >
                  {feature.publisher}
                </a>
                , "{feature.headline}"
              </li>
            ))}
            <li>
              <a
                href="https://councils.forbes.com/profile/Shariq-Refai-MD-MBA-Board-Certified-Psychiatrist-%7C-Founder-CEO-shrinkMD-shrinkMD/c1649312-6792-4cd3-b764-df4c393ec6c7"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
              >
                Forbes Business Council Profile
              </a>
            </li>
          </ul>
          <Link
            href="/media"
            className="inline-flex h-12 items-center justify-center border border-border bg-transparent text-primary px-8 text-sm font-medium hover:bg-accent/5 hover:border-accent transition-colors"
          >
            Press Resources
          </Link>
        </div>
      </section>

      {/* Topical Authority Block */}
      <section className="topical-authority py-20 md:py-24 px-4 md:px-8 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            What Dr. Refai Covers
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mt-6 mb-6">
            Areas of Focus
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-8">
            Dr. Refai writes and speaks across psychiatry, anxiety,
            overthinking, the high-performer pattern, the limits of
            popular mental health advice, modern psychiatry, telepsychiatry,
            and emotional regulation. The work is for adults who've already
            tried the apps and the books and still can't quiet the noise in
            their own heads.
          </p>
          <ul className="grid sm:grid-cols-2 gap-3 list-none p-0 text-secondary font-light">
            <li>&middot; Anxiety, panic, and the nervous system</li>
            <li>&middot; Overthinking, rumination, and intrusive thoughts</li>
            <li>&middot; High-functioning anxiety and the performer pattern</li>
            <li>&middot; Sports and performance psychiatry</li>
            <li>&middot; Modern psychiatry and digital mental health</li>
            <li>&middot; Telepsychiatry access and the standard of care</li>
            <li>&middot; Emotional regulation and self-awareness</li>
            <li>&middot; Mental health literacy for the general public</li>
          </ul>
        </div>
      </section>

      {/* Latest Perspectives Teaser */}
      <section className="latest-perspectives py-20 md:py-24 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-4xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            From the Journal
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mt-6 mb-8">
            Latest Perspectives
          </h2>
          <ul className="grid md:grid-cols-2 gap-6 list-none p-0">
            <li>
              <Link
                href="/perspectives/why-your-mind-feels-loudest-when-trying-to-rest"
                className="group block p-6 border border-border hover:border-accent transition-colors h-full"
              >
                <h3 className="font-serif text-lg text-primary group-hover:text-accent transition-colors">
                  Why Your Mind Feels Loudest When You're Trying to Rest
                </h3>
              </Link>
            </li>
            <li>
              <Link
                href="/perspectives/why-thinking-harder-usually-makes-anxiety-worse"
                className="group block p-6 border border-border hover:border-accent transition-colors h-full"
              >
                <h3 className="font-serif text-lg text-primary group-hover:text-accent transition-colors">
                  Why Thinking Harder Usually Makes Anxiety Worse
                </h3>
              </Link>
            </li>
            <li>
              <Link
                href="/perspectives/why-high-performers-can-still-feel-anxious"
                className="group block p-6 border border-border hover:border-accent transition-colors h-full"
              >
                <h3 className="font-serif text-lg text-primary group-hover:text-accent transition-colors">
                  Why High Performers Can Still Feel Anxious
                </h3>
              </Link>
            </li>
            <li>
              <Link
                href="/perspectives/what-modern-psychiatry-gets-right"
                className="group block p-6 border border-border hover:border-accent transition-colors h-full"
              >
                <h3 className="font-serif text-lg text-primary group-hover:text-accent transition-colors">
                  What Modern Psychiatry Gets Right
                </h3>
              </Link>
            </li>
          </ul>
          <div className="mt-8">
            <Link
              href="/perspectives"
              className="inline-flex h-12 items-center justify-center border border-border bg-transparent text-primary px-8 text-sm font-medium hover:bg-accent/5 hover:border-accent transition-colors"
            >
              Browse all essays
            </Link>
          </div>
        </div>
      </section>

      {/* Editorial Standards Strip */}
      <section className="editorial-standards py-20 md:py-24 px-4 md:px-8 bg-secondary text-white">
        <div className="container mx-auto max-w-4xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            Editorial Standards
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-white leading-tight mt-6 mb-6">
            How the Work Is Made
          </h2>
          <p className="text-white/85 font-light leading-relaxed mb-6">
            Every essay, book page, and educational piece on shariqrefai.com
            is authored by Shariq Refai, MD, MBA, and editorially reviewed
            before publication. Each essay carries a published date and a
            last-reviewed date. Where claims rely on specific research,
            sources are linked inline or listed in a References section.
            The site doesn't run ads, take affiliate commissions, or accept
            sponsored content.
          </p>
          <Link
            href="/editorial-process"
            className="inline-flex h-12 items-center justify-center border border-white/40 bg-transparent text-white px-8 text-sm font-medium hover:bg-white/10 hover:border-white transition-colors"
          >
            Read the editorial process
          </Link>
        </div>
      </section>

      {/* Why "shrink"? */}
      <section className="why-shrink py-20 md:py-24 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            A Founder Note
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mt-6 mb-6">
            Why &ldquo;shrink&rdquo;?
          </h2>
          <div className="space-y-5 text-lg text-secondary font-light leading-relaxed">
            <p>
              "Shrink" is what people actually say when they're talking about
              seeing a psychiatrist. The clinical word is precise and
              unwelcoming. The wellness word is friendly and vague. "Shrink" sits
              in between. It lowers the activation energy on a conversation that's
              already heavy. I'm not using it to trivialize the work. I'm using it
              because the work is more accessible when the door is named in a word
              a friend would use to a friend.
            </p>
          </div>
          <Link
            href="/why-shrink"
            className="mt-8 inline-flex items-center text-sm font-medium tracking-wide text-accent hover:text-primary transition-colors"
          >
            Read the full note &rarr;
          </Link>
        </div>
      </section>

      {/* Resources for Specific Conditions (Sister Publications) */}
      <section className="resources-sister py-20 md:py-24 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-4xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            Sister Publications
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mt-6 mb-6">
            Resources for Specific Conditions
          </h2>
          <p className="text-secondary font-light leading-relaxed mb-8">
            For deeper, condition-specific reading, Dr. Refai serves as
            medical reviewer for four independent editorial publications.
            None is a clinical service; all are educational reading
            sites.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a
              href={withNetworkUtm("https://anxietyresource.org/", "home_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="group block p-8 border border-border hover:border-accent transition-colors"
            >
              <h3 className="font-serif text-xl text-primary group-hover:text-accent transition-colors mb-2">
                AnxietyResource.org
              </h3>
              <p className="text-secondary font-light leading-relaxed text-sm">
                A clinically reviewed editorial publication on anxiety,
                with a 50-entry plain-language glossary and a complete
                state-by-state directory.
              </p>
            </a>
            <a
              href={withNetworkUtm("https://depressionresource.org/", "home_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="group block p-8 border border-border hover:border-accent transition-colors"
            >
              <h3 className="font-serif text-xl text-primary group-hover:text-accent transition-colors mb-2">
                DepressionResource.org
              </h3>
              <p className="text-secondary font-light leading-relaxed text-sm">
                A long-form depression library including screening tools,
                safety plan templates, and crisis resources.
              </p>
            </a>
            <a
              href={withNetworkUtm("https://anxietyresearch.org/", "home_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="group block p-8 border border-border hover:border-accent transition-colors"
            >
              <h3 className="font-serif text-xl text-primary group-hover:text-accent transition-colors mb-2">
                AnxietyResearch.org
              </h3>
              <p className="text-secondary font-light leading-relaxed text-sm">
                An editorial publication translating current anxiety research
                into plain-language summaries for general readers.
              </p>
            </a>
            <a
              href={withNetworkUtm("https://psychiatryrx.org/", "home_page")}
              target="_blank"
              rel="noopener noreferrer"
              className="group block p-8 border border-border hover:border-accent transition-colors"
            >
              <h3 className="font-serif text-xl text-primary group-hover:text-accent transition-colors mb-2">
                PsychiatryRx.org
              </h3>
              <p className="text-secondary font-light leading-relaxed text-sm">
                Plain-language, psychiatrist-reviewed guides to psychiatric
                and sleep medications, sourced from FDA labeling and clinical
                guidelines.
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* About the Network */}
      <section className="about-network py-16 md:py-20 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            About the Network
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mt-6 mb-6">
            Why does Dr. Refai have a separate personal site?
          </h2>
          <div className="space-y-4 text-secondary font-light leading-relaxed">
            <p>Mental health isn't one thing.</p>
            <p>
              Clinical care, educational publications, self-guided programs, and
              the work of a single psychiatrist shouldn't share the same address.
            </p>
            <p>
              Each property in{" "}
              <a
                href={withNetworkUtm("https://shrinknetwork.com/", "home_page")}
                rel="noopener"
                className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent transition-colors"
              >
                The Shrink Network
              </a>{" "}
              has its own job, and the founder's site has its own job too: this
              is where Dr. Refai's writing, books, media, and speaking live.
            </p>
            <p>
              A single site that tries to be a clinic, a library, and a person's
              professional page at once ends up doing none of them well.
            </p>
            <p className="font-serif italic text-primary">
              shariqrefai.com is the personal site. The rest of The Shrink
              Network is built around the work.
            </p>
          </div>
          <a
            href={withNetworkUtm("https://shrinknetwork.com/why/", "home_page")}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center text-sm font-medium tracking-wide text-accent hover:text-primary transition-colors"
          >
            Read why so many sites &rarr;
          </a>
        </div>
      </section>

      {/* Continue Into The Network */}
      <section className="continue-into-network py-20 md:py-24 px-4 md:px-8 bg-background">
        <div className="container mx-auto max-w-3xl">
          <NetworkRouter
            eyebrow="Where to go next"
            heading="Continue into the network."
            intro="Each property in the network has its own job. Pick the one that matches the question you're carrying right now."
            columns={3}
            cards={[
              {
                label: "shrinkMD",
                description: "Independent multistate telepsychiatry. Care lives here.",
                href: withNetworkUtm("https://shrinkmd.com/", "home_page"),
              },
              {
                label: "Shrinkopedia",
                description: "The clinically reviewed mental health encyclopedia.",
                href: withNetworkUtm("https://shrinkopedia.com/", "home_page"),
              },
              {
                label: "AnxietyResource",
                description: "Practical, condition-specific anxiety guidance.",
                href: withNetworkUtm("https://anxietyresource.org/", "home_page"),
              },
              {
                label: "DepressionResource",
                description: "Practical, condition-specific depression guidance.",
                href: withNetworkUtm("https://depressionresource.org/", "home_page"),
              },
              {
                label: "PsychiatryRx",
                description: "Plain-language medication guides.",
                href: withNetworkUtm("https://psychiatryrx.org/", "home_page"),
              },
              {
                label: "shrinQ",
                description: "Structured educational system for overthinking.",
                href: withNetworkUtm("https://shrinq.com/", "home_page"),
              },
              {
                label: "ShrinkDaily",
                description: "One evidence-based mental health concept a day.",
                href: withNetworkUtm("https://shrinkdaily.com/", "home_page"),
              },
              {
                label: "Shrinktionary",
                description: "Plain-language definitions of mental health terms.",
                href: withNetworkUtm("https://shrinktionary.com/", "home_page"),
              },
              {
                label: "QuitScrolling",
                description: "The attention app for spending less time scrolling.",
                href: withNetworkUtm("https://quitscrolling.app/", "home_page"),
              },
              {
                label: "shrinkiatry",
                description: "The culture, business, and practice of psychiatry.",
                href: withNetworkUtm("https://shrinkiatry.com/", "home_page"),
              },
            ]}
          />
          <a
            href={withNetworkUtm("https://shrinknetwork.com/", "home_page")}
            rel="noopener"
            className="mt-10 inline-flex h-12 items-center justify-center bg-primary text-primary-foreground px-8 text-sm font-medium hover:bg-secondary transition-colors"
          >
            See the full network &rarr;
          </a>
        </div>
      </section>

      {/* Visible FAQ */}
      <section className="homepage-faq py-20 md:py-24 px-4 md:px-8 bg-muted/30">
        <div className="container mx-auto max-w-3xl">
          <span className="text-accent text-xs font-bold tracking-[0.25em] uppercase">
            Common Questions
          </span>
          <h2 className="text-3xl md:text-4xl font-serif text-primary leading-tight mt-6 mb-10">
            Frequently Asked
          </h2>
          <dl className="grid gap-8">
            <div>
              <dt className="font-serif text-lg text-primary mb-2">
                Is shariqrefai.com a clinical service?
              </dt>
              <dd className="text-secondary font-light leading-relaxed">
                No. This is an educational, author, publishing, media, and
                founder platform. For clinical care, visit{" "}
                <a
                  href={withNetworkUtm("https://shrinkmd.com/", "home_page")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
                >
                  shrinkmd.com
                </a>
                , the separate telepsychiatry practice Dr. Refai founded.
              </dd>
            </div>
            <div>
              <dt className="font-serif text-lg text-primary mb-2">
                Does Dr. Refai accept new patients through this site?
              </dt>
              <dd className="text-secondary font-light leading-relaxed">
                No. New patient inquiries should go to shrinkmd.com.
              </dd>
            </div>
            <div>
              <dt className="font-serif text-lg text-primary mb-2">
                Can I cite or quote content from this site?
              </dt>
              <dd className="text-secondary font-light leading-relaxed">
                Yes, up to 200 words with attribution and a link back to
                the source URL. For full reprints, see the{" "}
                <Link
                  href="/media"
                  className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
                >
                  media page
                </Link>
                .
              </dd>
            </div>
            <div>
              <dt className="font-serif text-lg text-primary mb-2">
                How often is content updated?
              </dt>
              <dd className="text-secondary font-light leading-relaxed">
                Every essay is reviewed at least once per year for clinical
                accuracy. New essays are added as they're written and
                edited. Each piece carries a published date and a
                last-reviewed date.
              </dd>
            </div>
            <div>
              <dt className="font-serif text-lg text-primary mb-2">
                How do I book Dr. Refai for media or speaking?
              </dt>
              <dd className="text-secondary font-light leading-relaxed">
                Use the{" "}
                <Link
                  href="/contact"
                  className="text-accent underline decoration-accent/40 underline-offset-4 hover:decoration-accent"
                >
                  contact form
                </Link>{" "}
                with "Media &amp; Press" or "Speaking Engagement" selected.
              </dd>
            </div>
          </dl>
          <div className="mt-10">
            <Link
              href="/faq"
              className="inline-flex h-12 items-center justify-center border border-border bg-transparent text-primary px-8 text-sm font-medium hover:bg-accent/5 hover:border-accent transition-colors"
            >
              See all FAQs
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA band (Master Brief Section 3 rule 14: repeat primary CTA at bottom) */}
      <section className="final-cta py-20 md:py-24 px-4 md:px-8 bg-secondary text-white border-t border-white/10">
        <div className="container mx-auto max-w-3xl text-center">
          <p className="text-[#58B8B1] text-xs font-bold tracking-[0.25em] uppercase">
            The Founder Layer
          </p>
          <h2 className="mt-6 font-serif text-3xl md:text-4xl leading-snug text-white">
            One founder. One clinical practice. One mental-health network.
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-base md:text-lg font-light leading-relaxed text-white/85">
            Everything I've written, built, or review as a psychiatrist lives here or one click from here.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link
              href="/projects"
              className="inline-flex h-12 items-center justify-center bg-white text-secondary px-8 text-sm font-medium hover:bg-[#58B8B1] hover:text-white transition-colors"
            >
              Explore my work
            </Link>
            <Link
              href="/media-inquiries"
              className="inline-flex h-12 items-center justify-center border border-white/30 bg-transparent text-white px-8 text-sm font-medium hover:bg-white hover:text-secondary transition-colors"
            >
              Media and speaking
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-background py-20 border-t border-border">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <NetworkNextStep
            campaign="home_next_step"
            destinations={["shrinkmd", "shrinq", "shrinkopedia"]}
          />
        </div>
      </section>
    </SiteLayout>
  );
}
